import $ from 'jquery';
import 'jquery-serializejson';
import Parsley from 'parsleyjs';
import BaseComponent from 'foundation/base-component';
import HtmlHelper from 'foundation/html-helper';
import errorsSummaryTemplate from '../html/errors-summary.hbs';
import api, {
    isValidationError,
    getValidationErrors,
    isResponseHandled
} from '../../../general/js/api';
import eventBus from '../../../general/js/event-bus';
import createEvent from '../../../general/js/create-event';
import pageSpinner from '../../../general/js/page-spinner';
import { EVENT_AFTER_SUBMIT, EVENT_ON_ERROR } from '../../recaptcha/js/recaptcha.component';
import GTM from '../../../general/js/gtm';
import {
    throttle, THROTTLE_MESSAGE,
    THROTTLE_TIME_INTERVAL,
    THROTTLE_RETRY_ATTEMPTS,
} from '../../../general/js/helpers';
import modalService from '../../../general/js/modal-service';

const ENCTYPE_URLENCODED = 'application/x-www-form-urlencoded';
// const ENCTYPE_MULTIPART = 'multipart/form-data';
const FORM_CONTROL_ATTRIBUTE = 'data-form-control';
const PARSLEY_SERVER_CONSTRAINT_NAME = 'server';

const ASYNC_ACTION_SAML = 'SamlRequest';

export default class FormComponent extends BaseComponent {
    constructor(el, id, { excludedFields, onServerValidationError } = {}) {
        super(el, id);
        this.$element = $(this.element);
        this.method = this.element.method || 'post';
        this.action = this.element.action;
        this.enctype = this.element.enctype || ENCTYPE_URLENCODED;
        this.excludedFields = excludedFields;
        this.onServerValidationErrorHook = onServerValidationError;

        if (this.element.nodeName !== 'FORM') {
            throw new Error('element must be a form');
        }

        this.isSubmiting = false;

        this.formControlSelector = `[${FORM_CONTROL_ATTRIBUTE}]`;
        this.serverInvalidFields = {};

        // reset form in case of success submit
        this.resetOnSuccess = ('resetOnSuccess' in this.element.dataset) ? this.element.dataset.resetOnSuccess !== 'false' : true;

        this.errorsSummary = this.refs.errorsSummary;
        this.submitElement = this.refs.submit;

        this._parsleyForm = null;
        this._trackCategory = null;
        this._trackAction = null;
        this._trackLabel = null;
        this.isFormTrackingSended = false;

        this.onSubmit = this.onSubmit.bind(this);
        this.onFieldError = this.onFieldError.bind(this);
        this.sendFormTracking = this.sendFormTracking.bind(this);
        this.sendFormFieldTracking = this.sendFormFieldTracking.bind(this);

        this.service = modalService;
        this.init();
    }

    init() {
        if (this.isAsync()) {
            this.getParsleyForm().on('form:submit', this.onSubmit);
        }

        this.getParsleyForm().on('field:error', this.onFieldError);

        if (this.isMustBeTracked()) {
            this.setUpFormTracking();
        }

        if (this.refs.serverErrors) {
            this.refs.serverErrors.classList.add('is-shown');

            let errors = {};
            try {
                errors = JSON.parse(this.getAttribute(this.refs.serverErrors, 'json'));
            } catch (error) {
                throw new Error('Form: Invalid server errors JSON');
            }

            this.renderServerErrors(errors);
        }
    }

    getParsleyForm() {
        eventBus.emit('hide-form-field-tooltip');

        if (this._parsleyForm === null) {
            this._parsleyForm = new Parsley.Factory(this.element, {
                inputs: Parsley.options.inputs + ',[data-parsley-splitdate],[data-parsley-checkboxes-group],[data-parsley-splitdate-req]',
                excluded: this.excludedFields || 'input[type=button], input[type=submit], input[type=reset], input[type=hidden], :hidden',
                errorClass: 'is-invalid',
                successClass: 'is-success',
                classHandler: (field) => field.$element.closest(this.formControlSelector),
                errorsContainer: (field) => field.$element.closest(this.formControlSelector),
                errorsWrapper: '<ul class="form-field__error-list"></ul>',
                errorTemplate: '<li class="form-field__error-item"></li>'
            });
        }
        return this._parsleyForm;
    }

    isMustBeTracked() {
        return this.getAttribute(this.element, 'track') === 'true' && this.getTrackCategory() !== null && this.getTrackAction() !== null;
    }

    getTrackCategory() {
        if (this._trackCategory === null) {
            this._trackCategory = this.getAttribute(this.element, 'track-category');
        }
        return this._trackCategory;
    }

    getTrackAction() {
        if (this._trackAction === null) {
            this._trackAction = this.getAttribute(this.element, 'track-action');
        }
        return this._trackAction;
    }

    getTrackLabel() {
        if (this._trackLabel === null) {
            this._trackLabel = this.getAttribute(this.element, 'track-label');
        }
        return this._trackLabel;
    }

    isAsync() {
        return this.getAttribute(this.element, 'sync') !== 'true';
    }

    onSubmit() {
        if (!this.isSubmiting) {
            this.isSubmiting = true;
            try {
                this.beforeSubmit();

                throttle(() => api({
                    method: this.method,
                    url: this.getUrl(),
                    data: this.getRequestParams()
                }), THROTTLE_TIME_INTERVAL, THROTTLE_RETRY_ATTEMPTS)
                    .then((response) => {
                        this.afterSubmit();
                        this.resetForm();
                        this.handleSuccessSubmit(response);
                    }, (error) => {
                        if (isValidationError(error)) {
                            const failedElements = [];
                            this.renderServerErrors(getValidationErrors(error), failedElements);
                            if (this.onServerValidationErrorHook) {
                                this.onServerValidationErrorHook(failedElements);
                            }
                        }
                        if (error && error.code && error.code === 429) {
                            this.forceHideErrorsSummary();
                            this.showThrottleMessage();
                        }
                        this.afterSubmit();
                    });
            } catch (err) {
                console.error(err);
            }
        }
        // prevent default form submit
        return false;
    }

    handleSuccessSubmit(response) {
        if (!isResponseHandled(response)) {
            if (response.data.action === ASYNC_ACTION_SAML) {
                const formString = response.data.content.match(/<form(.+)((\s)+(.+))+<\/form>/i);

                if (!formString || !formString.length) {
                    throw new Error('Server doesn\'t returned form');
                }

                const syncForm = HtmlHelper.createElement(formString[0]);

                document.body.appendChild(syncForm);
                syncForm.submit();
            }
        }
        this.service.closePreviousModal(false);
    }

    renderServerErrors(serverErrors, failedElements = []) {
        const groupedErrors = {};

        if (serverErrors) {
            serverErrors.forEach((error) => {
                groupedErrors[error.key] = error.value;
            });
        }

        let hasFieldErrors = false;

        this.getParsleyForm().fields.forEach((field) => {
            const element = field.$element[0];
            const fieldName = element.name ?
                element.name : element.getAttribute('data-dc-form-field-name');

            if (fieldName in groupedErrors) {
                hasFieldErrors = true;

                field.$element.attr(`data-parsley-${PARSLEY_SERVER_CONSTRAINT_NAME}`, 'true');

                const errors = groupedErrors[fieldName];
                delete groupedErrors[fieldName];

                // programmatically set constraint error text
                field.options.serverMessage = errors.join(', ');
                // refresh stored value
                delete field.serverErrorValue;

                field.options.validateIfEmpty = true;

                if (FormComponent.isCaptchaField(field)) {
                    field.element.dispatchEvent(createEvent(EVENT_ON_ERROR));
                }

                failedElements.push(field.element);
            }
        });

        // validate to cause an error if we have field errors
        if (hasFieldErrors) {
            this.getParsleyForm().validate();
        }

        this.showErrorsSummary(groupedErrors);
    }

    showErrorsSummary(groupped) {
        if (this.errorsSummary) {
            if (Object.keys(groupped).length) {
                const errors = Object.keys(groupped).reduce((groupErrors, key) =>
                    [...groupErrors, ...groupped[key]], []);
                this.errorsSummary.innerHTML = errorsSummaryTemplate({ errors });
                this.errorsSummary.classList.add('is-shown');
            } else {
                this.hideErrorsSummary();
            }
        }
    }

    showErrorsSummaryFromArray(errors) {
        if (this.errorsSummary) {
            if (errors.length > 0) {
                this.errorsSummary.innerHTML = errorsSummaryTemplate({ errors });
                this.errorsSummary.classList.add('is-shown');
            } else {
                this.hideErrorsSummary();
            }
        }
    }

    hideErrorsSummary() {
        if (this.errorsSummary) {
            this.errorsSummary.classList.remove('is-shown');
        }
    }

    forceHideErrorsSummary() { //this.errorsSummary is undefined in this case
        if (this.refs.serverErrors) {
            this.refs.serverErrors.classList.remove('is-shown');
        }
    }

    showThrottleMessage() {
        if (this.refs.throttleMessage) {
            this.refs.throttleMessage.classList.add('is-shown');
        }
    }

    removeThrottleMessage() {
        if (this.refs.throttleMessage) {
            this.refs.throttleMessage.classList.remove('is-shown');
        }
    }

    getRequestParams() {
        let data = this.getFormData();
        if (this.enctype === ENCTYPE_URLENCODED) {
            data = this.getUrlencodedFormData(data);
        }
        return data;
    }

    getFormData() {
        const formData = new FormData(this.element);
        const uncheckedCheckboxesData = this.getUncheckedCheckboxesData();
        // patch default form data with unchecked single checkboxes
        Object.keys(uncheckedCheckboxesData).map((name) =>
            formData.append(name, uncheckedCheckboxesData[name]));
        return formData;
    }

    getUncheckedCheckboxesData() {
        const checkboxesInputs = [...this.element.querySelectorAll('input[type="checkbox"]')];

        function isArrayOrObject(name) {
            return /(.+)\[(.*)\]/.test(name);
        }

        return checkboxesInputs.reduce((result, checkbox) => {
            if (!checkbox.checked && !isArrayOrObject(checkbox.name)) {
                result[checkbox.name] = false;
            }
            return result;
        }, {});
    }

    getUrlencodedFormData(formData) {
        const result = [];
        const entries = formData.entries();

        // iterate over entries (IE compatible way :( )
        while (true) {
            const entry = entries.next();
            if (entry.done) break;
            const [key, value] = entry.value;
            const encodedEntry = encodeURIComponent(key) + '=' + encodeURIComponent(value == null ? '' : value);
            result.push(encodedEntry);
        }
        return result.join('&');
    }

    getUrl() {
        return this.action;
    }

    setUpFormTracking() {
        this.getParsleyForm().on('form:success', this.sendFormTracking);
        this.element.addEventListener('focusin', this.sendFormFieldTracking);
    }

    sendFormTracking() {
        if (!this.isFormTrackingSended) {
            this.isFormTrackingSended = true;
            GTM.push(this.getTrackCategory(), this.getTrackAction(), this.getTrackLabel());
        }
    }

    sendFormFieldTracking(e) {
        const target = e.target;
        if (this.refs.trackFields && this.refs.trackFields.indexOf(target) !== -1) {
            const label = this.getAttribute(target, 'track-label');
            if (label) {
                GTM.push(this.getTrackCategory(), this.getTrackAction(), label);
            }
        }
    }

    onSubmitClick() {
        // hook for descendants
    }

    onBeforeSubmit() {
        // hook for descendants
        // can be used for showing preloader
    }

    onAfterSubmit() {
        // hook for descendants
        // can be used for hiding preloader
    }

    onAfterReset() {
        // hook for descendants
    }

    afterSubmit() {
        pageSpinner.hide();
        if (this.submitElement) {
            this.submitElement.disabled = false;
        }
        this.handleCaptchaInputs();
        this.isSubmiting = false;
        this.onAfterSubmit();
    }

    beforeSubmit() {
        this.removeThrottleMessage();
        pageSpinner.show();
        if (this.submitElement) {
            this.submitElement.disabled = true;
        }
        this.onBeforeSubmit();
    }

    resetForm() {
        if (this.resetOnSuccess) {
            // slow down a little so modal will be able to be hidden before reset
            setTimeout(() => {
                this.element.reset();
                this.getParsleyForm().reset();
                this.onAfterReset();
            }, 1000);
        }

        this.hideErrorsSummary();
    }

    handleCaptchaInputs() {
        const captchas = this.getParsleyForm().fields.filter((field) =>
            FormComponent.isCaptchaField(field));

        captchas.forEach((field) => {
            // reset ui state of parsley for every captcha field
            field.reset();
            field.element.dispatchEvent(createEvent(EVENT_AFTER_SUBMIT));
        });
    }

    onDestroy() {
        this.getParsleyForm().destroy();
    }

    onFieldError(event) {
        // a11y fix
        event.$element.attr('aria-describedby', event._ui.errorsWrapperId);

        if (event.$element[0].hasAttribute('data-parsley-splitdate')) {
            [...event.$element[0].children].forEach((childNode) => {
                childNode.setAttribute('aria-describedby', event._ui.errorsWrapperId);
            });
        }
    }

    static isCaptchaField(field) {
        return 'recaptcha' in field.constraintsByName;
    }

    static hasFieldValue(field) {
        return (field.element.tagName === 'INPUT' && (field.element.type !== 'file')) || field.element.tagName === 'TEXTAREA' || field.element.tagName === 'SELECT';
    }
}

Parsley.addValidator('recaptcha', {
    validateString: (value) => /\S/.test(value),
    priority: 512,
    messages: {
        en: 'Please confirm that you are not a robot',
    }
});

Parsley.addValidator(PARSLEY_SERVER_CONSTRAINT_NAME, {
    validateString: (value, c, field) => {
        // if field cant store value we show error just once
        if (!FormComponent.hasFieldValue(field)) {
            if (field.serverErrorValue !== true) {
                field.serverErrorValue = true;
                return false;
            }

            return true;
        }

        // otherwise field is valid only if new value do not matches old one
        if ('serverErrorValue' in field) {
            return field.serverErrorValue !== field.getValue();
        }

        field.serverErrorValue = field.getValue();
        return false;
    },
    validateMultiple: (values, c, field) => {
        if (field.serverErrorValue !== true) {
            field.serverErrorValue = true;
            return false;
        }

        return true;
    },
    priority: 1024
});

Parsley.addValidator('splitdate', {
    validate: (value, requirement, instance) => {
        const dayNode = instance.element.querySelector('[data-dc-form-date-ref="day"]');
        const monthNode = instance.element.querySelector('[data-dc-form-date-ref="month"]');
        const yearNode = instance.element.querySelector('[data-dc-form-date-ref="year"]');
        const minAge = instance.element.getAttribute('data-min-age') || 18;
        const minBirthYear = instance.element.getAttribute('data-min-birth-year') || 1918;

        if (!yearNode.value || !monthNode.value || !dayNode.value) return false;

        if (yearNode.value && yearNode.value < minBirthYear) return false;

        const validatingDate = new Date(yearNode.value, monthNode.value - 1, dayNode.value);
        const maxDate = new Date();
        maxDate.setFullYear(maxDate.getFullYear() - minAge);
        maxDate.setHours(0, 0, 0, 0);

        if (Number.isNaN(validatingDate.getTime())) return false;

        return validatingDate <= maxDate;
    },
    priority: 512
});

Parsley.addValidator('splitdateReq', {
    validate: (value, requirement, instance) => {
        const dayNode = instance.element.querySelector('[data-dc-form-date-ref="day"]');
        const monthNode = instance.element.querySelector('[data-dc-form-date-ref="month"]');
        const yearNode = instance.element.querySelector('[data-dc-form-date-ref="year"]');
        const validatingDate = new Date(yearNode.value, monthNode.value - 1, dayNode.value);

        if (!yearNode.value || !monthNode.value || !dayNode.value) return false;

        return !Number.isNaN(validatingDate.getTime());
    },
    priority: 512
});

Parsley.addValidator('checkboxesGroup', {
    validateMultiple: (values) => values.length > 0,
    priority: 512,
});
